import imageSegmentationApi from "@frontend/services/api/image-segmentation";
import {
  imageSegmentationEventBroker,
  IMAGE_SEGMENTATION_EVENTS
} from "@frontend/group/modules/image-segmentation/event-broker";

// Cache for completed segmentation results - keyed by instanceId
const segmentationCache = new Map();

/**
 * Extract clean file path from a URL that may contain /share?file= or other parameters
 */
function extractCleanFilePath(url) {
  if (!url) return null;

  // If it's a /share?file= URL, extract just the file path
  if (url.includes('/share?file=') || url.includes('/share%3Ffile=')) {
    try {
      const urlObj = new URL(url, window.location.origin);
      let filePath = urlObj.searchParams.get('file');
      if (filePath) {
        // Decode if needed and remove any trailing parameters
        filePath = decodeURIComponent(filePath).split('&')[0];
        return filePath;
      }
    } catch (e) {
      // If URL parsing fails, try regex
      const match = url.match(/[?&]file=([^&]+)/);
      if (match) {
        return decodeURIComponent(match[1]);
      }
    }
  }

  // If it's already a clean path (starts with user-folder or similar), return as-is
  if (url.startsWith('user-folder/') || url.startsWith('/storage/')) {
    return url;
  }

  return url;
}

/**
 * Get the background image info from a template preview
 * Returns an object with path, actual displayed dimensions, and segmentation job UUID if exists
 */
function getTemplateBackgroundImageInfo(preview) {
  if (!preview) {
    return null;
  }

  let imagePath = null;
  let displayedWidth = null;
  let displayedHeight = null;
  let imageObject = null;
  let segmentationJobUuid = null;

  // Check if this template was created from segmentation (has cached job UUID)
  if (preview.data?.segmentation_job_uuid) {
    segmentationJobUuid = preview.data.segmentation_job_uuid;
  }

  // First try to get from preview.data.background array
  if (preview.data?.background && preview.data.background.length > 0) {
    imagePath = preview.data.background[0];
  }
  // Try additional_background_images array
  else if (preview.data?.additional_background_images && preview.data.additional_background_images.length > 0) {
    imagePath = preview.data.additional_background_images[0];
  }

  // Try to get dimensions from canvas objects
  if (preview.canvas) {
    const objects = preview.canvas.getObjects();

    for (const obj of objects) {
      // Check for any background theme image objects (including additional ones)
      if ((obj.id?.startsWith('bk_theme_img_') || obj.id?.startsWith('add_bk_theme_img_')) && obj._element?.src) {
        if (!imagePath) {
          imagePath = obj._element.src;
        }
        imageObject = obj;
        break;
      }
      // Also check by fieldType property
      if (obj.fieldType === 'Background Theme Image' && obj._element?.src) {
        if (!imagePath) {
          imagePath = obj._element.src;
        }
        imageObject = obj;
        break;
      }
      // Also check by templateField type
      if (obj.templateField?.type === 'Background Theme Image' && obj._element?.src) {
        if (!imagePath) {
          imagePath = obj._element.src;
        }
        imageObject = obj;
        break;
      }
    }

    // Fallback: try to get from canvas background
    if (!imagePath && preview.canvas.backgroundImage?._element?.src) {
      imagePath = preview.canvas.backgroundImage._element.src;
      imageObject = preview.canvas.backgroundImage;
    }
  }

  // Get displayed dimensions from the canvas object
  if (imageObject) {
    // getScaledWidth/Height returns the actual displayed size including scale
    displayedWidth = Math.round(imageObject.getScaledWidth());
    displayedHeight = Math.round(imageObject.getScaledHeight());
  }

  if (!imagePath) {
    return null;
  }

  return {
    path: extractCleanFilePath(imagePath),
    displayedWidth,
    displayedHeight,
    segmentationJobUuid
  };
}

export async function startCreateLayersForSelectedImage(instance) {
  const canvas = instance?.canvas;
  const image = canvas?.getActiveObject?.();
  const isImage = image && (
    image.fieldType === 'Background Theme Image'
    || image.templateField?.type === 'Background Theme Image'
    || image.id?.startsWith('bk_theme_img_')
    || image.id?.startsWith('add_bk_theme_img_')
  );

  if (!isImage || !image._element?.src) {
    toastr.warning('Select an image before creating layers');
    return;
  }

  const additionalImageMatch = image.id?.match(/^add_bk_theme_img_(\d+)$/);
  const sourceImageIsAdditional = Boolean(additionalImageMatch);
  const sourceImageIndex = additionalImageMatch
    ? Number(additionalImageMatch[1])
    : null;

  const $grid = $('#template-group-preview');
  return startCreateLayers({
    customerId: $grid.attr('data-customer-id'),
    layoutId: $grid.attr('data-layout-id'),
    instanceId: instance.id,
    replaceTemplateInstanceId: instance.id,
    imageInfo: {
      path: extractCleanFilePath(image._element.src),
      displayedWidth: Math.round(image.getScaledWidth()),
      displayedHeight: Math.round(image.getScaledHeight()),
      sourceImageLeft: image.getPointByOrigin
        ? image.getPointByOrigin('left', 'top').x
        : image.left - (image.getScaledWidth() / 2),
      sourceImageTop: image.getPointByOrigin
        ? image.getPointByOrigin('left', 'top').y
        : image.top - (image.getScaledHeight() / 2),
      sourceImageIsAdditional,
      sourceImageIndex,
      segmentationJobUuid: instance.template?.settings?.segmentation_job_uuid
        || instance.data?.segmentation_job_uuid
        || null,
    },
  });
}

/**
 * Handle click on "Create Layers..." button
 */
export async function startCreateLayers({
  customerId,
  layoutId,
  layoutName = null,
  instanceId = null,
  imageInfo,
  replaceTemplateInstanceId = null,
}) {
  if (!imageInfo || !imageInfo.path) {
    toastr.error('Could not find the selected image');
    return;
  }

  try {
    const { data } = await imageSegmentationApi.startSegmentation(
      customerId,
      imageInfo.path,
      layoutId,
      layoutName,
      imageInfo.displayedWidth,
      imageInfo.displayedHeight,
      imageInfo.segmentationJobUuid,
      false,
      imageInfo.sourceImageLeft,
      imageInfo.sourceImageTop
    );

    const previewData = {
      jobUuid: data.job_uuid,
      customerId,
      imagePath: imageInfo.path,
      layoutId,
      layoutName,
      instanceId,
      replaceTemplateInstanceId,
      originalImagePath: data.original_image_path,
      displayedWidth: data.displayed_width || imageInfo.displayedWidth,
      displayedHeight: data.displayed_height || imageInfo.displayedHeight,
      sourceImageLeft: imageInfo.sourceImageLeft,
      sourceImageTop: imageInfo.sourceImageTop,
      sourceImageIsAdditional: imageInfo.sourceImageIsAdditional,
      sourceImageIndex: imageInfo.sourceImageIndex,
    };

    if (data.success !== false && data.job_uuid) {
      if (data.cached && data.layer_data) {
        imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_CACHED_PREVIEW, {
          ...previewData,
          stale: data.stale === true,
          cachedData: {
            layers: data.layer_data,
            inpaintedBackgroundUrl: data.inpainted_background_path || '',
          },
        });
      } else {
        imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_PREVIEW, {
          ...previewData,
          originalImageUrl: imageInfo.path,
        });
      }
    } else {
      toastr.error('Failed to start image segmentation');
    }
  } catch (e) {
    toastr.error(e.response?.data?.message || 'Something went wrong while starting segmentation');
    console.error('Create layers error:', e);
  }
}

async function handleCreateLayersClick(instanceId) {
  // Get layout_id and customer_id from DOM
  const $grid = $('#template-group-preview');
  const layoutId = $grid.attr('data-layout-id');
  const customerId = $grid.attr('data-customer-id');

  // Find the preview for this template from window.previews
  const previews = window.previews || [];
  const preview = previews.find(p => p.id === instanceId);

  if (!preview) {
    toastr.error('Could not find template preview');
    return;
  }

  // Get the background image info from the template
  const imageInfo = getTemplateBackgroundImageInfo(preview);
  if (!imageInfo || !imageInfo.path) {
    toastr.error('Could not find background image in template. Please ensure the template has a background image.');
    return;
  }

  // Check if we have cached results for this template
  const cachedResult = segmentationCache.get(instanceId);
  if (cachedResult && cachedResult.imagePath === imageInfo.path) {
    // Use cached results - instantly show preview without re-parsing
    imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_CACHED_PREVIEW, {
      jobUuid: cachedResult.jobUuid,
      customerId: customerId,
      imagePath: imageInfo.path,
      layoutId: layoutId,
      instanceId: instanceId,
      cachedData: cachedResult.data,
      stale: cachedResult.stale === true,
      displayedWidth: imageInfo.displayedWidth,
      displayedHeight: imageInfo.displayedHeight,
    });
    return;
  }

  try {
    // Start segmentation job with the layout ID and displayed dimensions
    // Also pass segmentationJobUuid if this template was created from a previous segmentation
    const { data } = await imageSegmentationApi.startSegmentation(
      customerId,
      imageInfo.path,
      layoutId, // Pass layout ID to create a copy of this layout
      null, // layoutName will be auto-generated
      imageInfo.displayedWidth,  // Pass actual displayed width
      imageInfo.displayedHeight,  // Pass actual displayed height
      imageInfo.segmentationJobUuid // Pass previous job UUID for cache lookup
    );

    if (data.success) {
      // Check if we got cached results from the backend (persistent cache)
      if (data.cached && data.layer_data) {
        // Use cached results - instantly show preview without re-parsing
        imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_CACHED_PREVIEW, {
          jobUuid: data.job_uuid,
          customerId: customerId,
          imagePath: imageInfo.path,
          layoutId: layoutId,
          instanceId: instanceId,
          originalImagePath: data.original_image_path, // Original image for re-analysis
          stale: data.stale === true, // Layers kept by this template predate the current analysis
          cachedData: {
            layers: data.layer_data,
            inpaintedBackgroundUrl: data.inpainted_background_path || '',
          },
          // Pass dimensions from backend cache or current template
          displayedWidth: data.displayed_width || imageInfo.displayedWidth,
          displayedHeight: data.displayed_height || imageInfo.displayedHeight,
        });
      } else {
        // Open the segmentation preview modal and wait for processing
        imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_PREVIEW, {
          jobUuid: data.job_uuid,
          customerId: customerId,
          imagePath: imageInfo.path,
          layoutId: layoutId,
          instanceId: instanceId, // Pass instanceId for session caching
          displayedWidth: imageInfo.displayedWidth,
          displayedHeight: imageInfo.displayedHeight,
        });
      }
    } else {
      toastr.error('Failed to start image segmentation');
    }
  } catch (e) {
    toastr.error(e.response?.data?.message || 'Something went wrong while starting segmentation');
    console.error('Create layers error:', e);
  }
}

/**
 * Save segmentation results to cache for a specific template instance
 */
export function cacheSegmentationResult(instanceId, imagePath, jobUuid, data, stale = false) {
  segmentationCache.set(instanceId, {
    imagePath,
    jobUuid,
    data,
    // Improving a layer does not make its analysis current, so the notice has to survive
    // being served from here.
    stale,
    timestamp: Date.now(),
  });
}

/**
 * Clear cached results for a specific template instance
 */
export function clearSegmentationCache(instanceId) {
  if (instanceId) {
    segmentationCache.delete(instanceId);
  } else {
    segmentationCache.clear();
  }
}

/**
 * Initialize the "Create Layers..." button handler for a template row
 */
export function initCreateLayersHandler($row) {
  const $button = $row.find('.create-layers');

  $button.on('click', function () {
    const id = $(this).attr('data-id');
    handleCreateLayersClick(id);
  });
}

/**
 * Process segmentation_text_fields stored in template settings.
 * Creates editable Multi Line Text fields for text_image layers that were converted.
 * Should be called after previews are drawn and additional fields are initialized.
 *
 * @param {Function} addAddField - The addAddField function from additional-fields module
 * @param {Function} updateLayoutChanges - Function to persist layout changes
 */
export async function processSegmentationTextFields(addAddField, updateLayoutChanges, previews) {
  previews = previews || window.previews || [];
  console.log('[SegTextFields] Processing previews:', previews.length, 'previews available');

  for (const preview of previews) {
    console.log('[SegTextFields] Preview', preview?.id, 'has segmentation_text_fields:', !!preview?.data?.segmentation_text_fields, preview?.data?.segmentation_text_fields);
    const hasTextFields = preview?.data?.segmentation_text_fields;
    if (!hasTextFields) {
      continue;
    }

    const textFields = preview.data.segmentation_text_fields;
    const instanceId = preview.id;

    for (const field of textFields) {
      try {
        const cleanText = (field.text || 'Text').replace(/<[^>]*>/g, '');
        const offsetX = Number(field.offset_x || 0);
        const offsetY = Number(field.offset_y || 0);
        const width = Number(field.width || 200);
        const height = Number(field.height || 50);

        await addAddField('Multi Line Text', instanceId, {
          currentPreview: preview,
          text: cleanText,
          _alignment: field.alignment || 'left',
          _angle: Number(field.angle || 0).toFixed(2),
          _color: field.color || '#000000',
          _fontsize: String(Math.round(field.font_size || 24)),
          _offset_x: offsetX.toFixed(2),
          _offset_y: offsetY.toFixed(2),
          _width: width.toFixed(2),
          _height: height.toFixed(2),
          _bounding_data: JSON.stringify({left: offsetX, top: offsetY, width, height}),
          _leading: '1',
          _opacity: '1',
        });
      } catch (e) {
        console.error('Failed to create text field from segmentation:', e, field);
      }
    }

    updateLayoutChanges(instanceId, 'segmentation_text_fields', null);
  }
}

export function triggerAutoSegmentation() {
  const previews = window.previews || [];
  if (previews.length > 0) {
    const firstPreview = previews[0];
    if (firstPreview && firstPreview.id) {
      setTimeout(() => {
        handleCreateLayersClick(firstPreview.id);
      }, 500);
    }
  }
}
